import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

interface BlobGlassCardProps {
  children: ReactNode;
  /** How many drifting blobs sit behind the glass. Scale to the tile size. */
  blobs?: number;
  /** 'navy' for dark sections, 'light' for white sections. */
  theme?: 'navy' | 'light';
  className?: string;
}

// A1 "Card_tile" from animation.md — frosted glass over slowly drifting cyan/blue
// blobs. Recolored to the TICCA navy/cyan system, opacity kept low, motion slow
// and calm (investor-grade). Respects prefers-reduced-motion.
const blobPresets = [
  { top: '-20%', left: '-10%', from: 'rgba(7,78,220,0.24)', dur: 16, path: { x: [0, 24, -12, 0], y: [0, -18, 14, 0] } },
  { top: '40%', left: '60%', from: 'rgba(255,140,0,0.20)', dur: 20, path: { x: [0, -20, 16, 0], y: [0, 16, -20, 0] } },
  { top: '65%', left: '5%', from: 'rgba(74,172,0,0.20)', dur: 22, path: { x: [0, 18, -22, 0], y: [0, -14, 18, 0] } },
  { top: '-10%', left: '70%', from: 'rgba(237,2,22,0.16)', dur: 18, path: { x: [0, -16, 20, 0], y: [0, 20, -16, 0] } },
];

export function BlobGlassCard({ children, blobs = 3, theme = 'navy', className = '' }: BlobGlassCardProps) {
  const reduce = useReducedMotion();
  const count = Math.max(1, Math.min(blobs, blobPresets.length));
  const surface = 'border-white/12 bg-slate-900/50 hover:border-white/22 hover:bg-slate-900/70';

  return (
    <div
      className={`group relative h-full overflow-hidden rounded-2xl border backdrop-blur-md transition-colors ${surface} ${className}`}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {blobPresets.slice(0, count).map((b, i) => (
          <motion.div
            key={i}
            className="absolute h-40 w-40 rounded-full blur-3xl"
            style={{ top: b.top, left: b.left, background: b.from }}
            animate={reduce ? undefined : { x: b.path.x, y: b.path.y }}
            transition={reduce ? undefined : { duration: b.dur, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
}
